/**
 * The Shows inbox — what needs the person, derived from APIs that already exist (SCRUM-587 S4, owner: «Inbox:
 * вещи, которым нужен пользователь»). Pure: no DOM, no fetch. The screen is `components/shows-inbox.ts`.
 *
 *   approval      an offer waits for MY approval — as the band (its rider is mine, status `submitted`) or as the
 *                 organiser (`awaiting_organiser`, my second slot `pending`);
 *   new_offer     an offer arrived on a show of mine that is not (yet) mine to approve — the organiser, while the
 *                 band has not answered;
 *   reconfirm     MY offer, as a supplier, whose rider changed: «rider changed — re-confirm»;
 *   declined      MY offer was declined (with the author's reason, when given);
 *   rider_request somebody asked me for my rider.
 *
 * ONE item per offer, the strongest reason first (approval > new_offer). Newest first.
 */
import { showNameWithVenue } from "./show-name";
import type { OfferSummaryResponse } from "../api/offers-api";
import { showSubject as showSubjectOf, type ShowWhen } from "@scb/schema";
import type { ShowDetail, ShowListRow, ShowOfferRow } from "../api/shows-api";

export type InboxKind = "approval" | "new_offer" | "reconfirm" | "declined" | "rider_request";

export type InboxTarget =
  | { kind: "show"; showId: string }
  | { kind: "offer"; riderId: string; version: number; offerId: string };

export interface InboxItem {
  /** Stable key: `<kind>:<offer or performance id>`. */
  id: string;
  kind: InboxKind;
  /** ISO time the thing happened; the list is sorted on it. */
  at: string;
  offerId?: string;
  /** The other person: the supplier's name or email; undefined when nobody is named. */
  who?: string;
  /** What it is about: the show's title, else its acts, else the rider's title. */
  subject: string;
  /** `approval` only: the first round is «new», a later one is «sent again». */
  isNew?: boolean;
  from?: number;
  to?: number;
  reason?: string;
  target: InboxTarget;
  /** The show this is about, when there is one (an offer not yet on a show has none). */
  showId?: string;
  /** The SHOW's date (not `at`, the time the thing arrived); `undefined` when the show is not in the list. */
  when?: ShowWhen;
  /** The show is a sketch: no date, no venue yet. */
  isSketch?: boolean;
  /** The show has news the person has not opened yet (the server's `unread`, `POST /shows/:id/read` clears it). */
  unread: boolean;
}

export interface InboxInput {
  myEmail: string | null;
  shows: readonly ShowListRow[];
  /** `GET /shows/:id` of the shows where the caller is an organiser or the band. */
  details: readonly ShowDetail[];
  incoming: readonly OfferSummaryResponse[];
  mine: readonly OfferSummaryResponse[];
}

/**
 * A supplier as the organiser and the band read it (owner, 06.10.2026, decision 37): the name, the company and the
 * email — "Name (Company, email)", "Company (email)", or just what is known; undefined when nothing is.
 */
export function supplierLabel(name: string | null | undefined, company: string | null | undefined, email?: string | null): string | undefined {
  const mail = email?.trim() || undefined;
  const firm = company?.trim() || undefined;
  const who = name?.trim() || undefined;
  const inside = [who ? firm : undefined, mail].filter((x): x is string => !!x);
  if (who) return inside.length ? `${who} (${inside.join(", ")})` : who;
  if (firm) return mail ? `${firm} (${mail})` : firm;
  return mail;
}

/** The show as the person calls it: its title, else the acts, else undefined. */
export function showSubject(row: Pick<ShowListRow, "title" | "performances"> | undefined): string | undefined {
  return row ? showSubjectOf(row) : undefined;
}

function toMs(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? 0 : ms;
}

/** What the badge on the nav says: nothing for 0, the number, «99+» past that. */
export function unreadBadgeText(count: number): string {
  if (!(count > 0)) return "";
  return count > 99 ? "99+" : String(count);
}

/**
 * How many shows have news (the server's `unread`; archived shows are not in `GET /shows`). This is the nav badge:
 * one request, no detail fetches. A show with two new offers counts once, the way the show list marks it.
 */
/** The «Inbox» badge (owner, 06.10: «в шоу пока не транслируем цифру, только в инбокс»): the inbox ROWS waiting
 *  for the person. Several new offers on one show are several rows — five read 5, never 1. */
export function countUnreadItems(items: readonly Pick<InboxItem, "unread">[]): number {
  return items.filter((i) => i.unread).length;
}

export function countUnreadShows(rows: readonly Pick<ShowListRow, "unread">[]): number {
  return rows.filter((r) => r.unread).length;
}

export function deriveInbox(input: InboxInput): InboxItem[] {
  const byShow = new Map(input.shows.map((s) => [s.id, s]));
  const items: Omit<InboxItem, "unread" | "showId" | "when" | "isSketch">[] = [];
  const claimed = new Set<string>(); // offers that already have their one item

  const offerTarget = (o: OfferSummaryResponse): InboxTarget =>
    o.showId ? { kind: "show", showId: o.showId } : { kind: "offer", riderId: o.riderId, version: o.riderVersion, offerId: o.id };

  // 1. The band's approval: the offers on riders I author, answered and waiting.
  for (const o of input.incoming) {
    if (o.selfOffer || o.status !== "submitted") continue;
    claimed.add(o.id);
    items.push({
      id: `approval:${o.id}`,
      kind: "approval",
      at: o.updatedAt,
      offerId: o.id,
      who: supplierLabel(o.supplierDisplayName, o.supplierCompany, o.supplierEmail),
      subject: showNameWithVenue(o.showId ? byShow.get(o.showId) : undefined) ?? o.riderTitle,
      isNew: o.round <= 1,
      target: offerTarget(o),
    });
  }

  // 2. The shows where I am the organiser or the band: the organiser's approval, new offers, rider requests.
  const me = input.myEmail?.trim().toLowerCase() ?? null;
  for (const d of input.details) {
    const showId = d.show.uuid;
    const subject = showNameWithVenue(byShow.get(showId)) ?? d.show.performances.map((p) => p.name).join(", ");
    const organiserOnly = d.roles.includes("organiser") && !d.roles.includes("band");
    const asOwner = d.roles.includes("organiser") || d.roles.includes("band");
    for (const o of d.offers as ShowOfferRow[]) {
      if (claimed.has(o.id) || !asOwner) continue;
      const who = supplierLabel(o.supplierDisplayName, o.supplierCompany, o.supplierEmail);
      if (organiserOnly && o.status === "awaiting_organiser" && o.organiserState === "pending") {
        claimed.add(o.id);
        items.push({ id: `approval:${o.id}`, kind: "approval", at: o.updatedAt, offerId: o.id, who, subject, isNew: o.round <= 1, target: { kind: "show", showId } });
      } else if (o.status === "submitted") {
        claimed.add(o.id);
        items.push({ id: `new_offer:${o.id}`, kind: "new_offer", at: o.updatedAt, offerId: o.id, who, subject, target: { kind: "show", showId } });
      }
    }
    if (d.roles.includes("band")) {
      for (const p of d.show.performances) {
        const req = p.rider_request;
        if (!req || req.state !== "sent") continue;
        if (me !== null && req.to_email.trim().toLowerCase() !== me) continue;
        items.push({ id: `rider_request:${p.uuid}`, kind: "rider_request", at: req.sent_at, subject, target: { kind: "show", showId } });
      }
    }
  }

  // 3. My own offers, as a supplier.
  for (const o of input.mine) {
    const subject = showNameWithVenue(o.showId ? byShow.get(o.showId) : undefined) ?? o.riderTitle;
    const target: InboxTarget = { kind: "offer", riderId: o.riderId, version: o.riderVersion, offerId: o.id };
    if (o.status === "declined") {
      items.push({ id: `declined:${o.id}`, kind: "declined", at: o.updatedAt, offerId: o.id, subject, ...(o.declineReason ? { reason: o.declineReason } : {}), target });
    } else if (o.riderChanged && o.status !== "approved" && o.status !== "draft" && o.status !== "withdrawn") {
      items.push({ id: `reconfirm:${o.id}`, kind: "reconfirm", at: o.updatedAt, offerId: o.id, subject, from: o.riderChanged.answered, to: o.riderChanged.current, target });
    }
  }

  const offerShow = new Map<string, string>();
  for (const o of [...input.incoming, ...input.mine]) if (o.showId) offerShow.set(o.id, o.showId);
  return items
    .map((item): InboxItem => {
      const showId = item.target.kind === "show" ? item.target.showId : item.offerId ? offerShow.get(item.offerId) : undefined;
      const row = showId ? byShow.get(showId) : undefined;
      return { ...item, ...(showId ? { showId } : {}), ...(row ? { when: row.when, isSketch: row.isSketch } : {}), unread: row?.unread === true };
    })
    .sort((a, b) => toMs(b.at) - toMs(a.at) || a.id.localeCompare(b.id));
}
